import { AdminLayout } from "./AdminLayout";
import { Users, Shield, Activity, Database, Mail, Link2, RefreshCw, Trash2 } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";

const Settings = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [gmailAccounts, setGmailAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('users');

  const fetchData = useCallback(async () => {
    try {
      if (user?.role === 'admin') {
        const [usersRes, logsRes, gmailRes] = await Promise.all([
          axios.get(`${API}/auth/users`, { withCredentials: true }),
          axios.get(`${API}/audit-logs`, { withCredentials: true }),
          axios.get(`${API}/gmail/accounts`, { withCredentials: true })
        ]);
        setUsers(usersRes.data);
        setAuditLogs(logsRes.data);
        setGmailAccounts(gmailRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch settings data:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const connectGmail = async () => {
    try {
      const redirectUri = `${window.location.origin}/admin/settings`;
      const res = await axios.get(`${API}/gmail/auth/url?redirect_uri=${encodeURIComponent(redirectUri)}`, { withCredentials: true });
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error("Failed to initiate Gmail connection");
    }
  };

  // Handle OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      const handleOAuthCallback = async () => {
        try {
          const redirectUri = `${window.location.origin}/admin/settings`;
          await axios.post(`${API}/gmail/auth/callback`, {
            code,
            redirect_uri: redirectUri
          }, { withCredentials: true });
          toast.success("Gmail account connected successfully!");
          // Clear the URL params
          window.history.replaceState({}, document.title, window.location.pathname);
          fetchData();
        } catch (error) {
          toast.error("Failed to connect Gmail account");
        }
      };
      handleOAuthCallback();
    }
  }, [fetchData]);

  const syncGmail = async (accountId) => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/gmail/sync/${accountId}`, {}, { withCredentials: true });
      toast.success(`Synced ${res.data.threads_synced} threads`);
      fetchData();
    } catch (error) {
      toast.error("Failed to sync Gmail");
    } finally {
      setSyncing(false);
    }
  };

  const disconnectGmail = async (accountId) => {
    try {
      await axios.delete(`${API}/gmail/accounts/${accountId}`, { withCredentials: true });
      toast.success("Gmail account disconnected");
      fetchData();
    } catch (error) {
      toast.error("Failed to disconnect Gmail");
    }
  };

  const updateUserRole = async (userId, role) => {
    try {
      await axios.put(`${API}/auth/users/${userId}/role?role=${role}`, {}, { withCredentials: true });
      toast.success("Role updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'users', name: 'Team Members', icon: Users },
    { id: 'integrations', name: 'Integrations', icon: Link2 },
    { id: 'audit', name: 'Audit Logs', icon: Activity },
    { id: 'gdpr', name: 'GDPR & Privacy', icon: Shield }
  ];

  if (user?.role !== 'admin') {
    return (
      <AdminLayout title="Settings">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Shield className="w-10 h-10 text-[#5F8FB8] mx-auto mb-4" />
            <h2 className="text-lg font-bold mb-2">Admin Access Required</h2>
            <p className="text-sm text-[#8A8A8A]">Contact your administrator for access.</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Settings">
      <div className="space-y-6" data-testid="admin-settings">
        {/* Tabs */}
        <div className="flex border-b border-[#2E2E2E]">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-[#5F8FB8] text-[#F5F4EF]'
                  : 'border-transparent text-[#8A8A8A] hover:text-[#F5F4EF]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.name}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold mb-1">Team Members</h2>
              <p className="text-sm text-[#6B7280]">Manage console access</p>
            </div>

            <div className="border border-[#2E2E2E]">
              <table className="w-full">
                <thead className="bg-[#1C1C1C] border-b border-[#2E2E2E]">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">User</th>
                    <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Email</th>
                    <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Role</th>
                    <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(3)].map((_, i) => (
                      <tr key={i} className="border-b border-[#2E2E2E]">
                        <td colSpan={4} className="p-4"><div className="h-4 bg-[#2E2E2E] rounded animate-pulse" /></td>
                      </tr>
                    ))
                  ) : users.length === 0 ? (
                    <tr><td colSpan={4} className="p-8 text-center text-[#8A8A8A]">No users found</td></tr>
                  ) : (
                    users.map((u) => (
                      <tr key={u.user_id} className="border-b border-[#2E2E2E] hover:bg-[#1C1C1C]">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            {u.picture ? (
                              <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full" />
                            ) : (
                              <div className="w-8 h-8 bg-[#2E2E2E] rounded-full" />
                            )}
                            <span className="font-medium">{u.name}</span>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-[#8A8A8A]">{u.email}</td>
                        <td className="p-4">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className={`badge cursor-pointer ${u.role === 'admin' ? 'bg-[#2F5D8C]/20 text-[#5F8FB8]' : 'bg-blue-500/20 text-blue-400'}`}>
                                {u.role}
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-[#1C1C1C] border-[#2E2E2E]">
                              <DropdownMenuItem onClick={() => updateUserRole(u.user_id, 'admin')} className="cursor-pointer hover:bg-[#2E2E2E]">
                                Admin
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => updateUserRole(u.user_id, 'staff')} className="cursor-pointer hover:bg-[#2E2E2E]">
                                Staff
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                        <td className="p-4 text-sm text-[#8A8A8A]">{formatDate(u.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'integrations' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold mb-1">Integrations</h2>
              <p className="text-sm text-[#6B7280]">Connect external services</p>
            </div>

            {/* Gmail Integration */}
            <div className="card-dashboard">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-500/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h3 className="font-medium">Gmail</h3>
                    <p className="text-xs text-[#6B7280]">Sync email threads to Comms</p>
                  </div>
                </div>
                <button 
                  onClick={connectGmail}
                  className="btn-primary text-sm"
                  data-testid="connect-gmail-btn"
                >
                  Connect
                </button>
              </div>

              {gmailAccounts.length > 0 && (
                <div className="border-t border-[#2E2E2E] pt-4 mt-4">
                  <h4 className="text-sm font-medium mb-3">Connected Accounts</h4>
                  <div className="space-y-3">
                    {gmailAccounts.map((account) => (
                      <div 
                        key={account.account_id} 
                        className="flex items-center justify-between p-3 bg-[#1C1C1C] rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Mail className="w-4 h-4 text-[#8A8A8A]" />
                          <div>
                            <span className="font-medium">{account.email}</span>
                            {account.last_sync && (
                              <p className="text-xs text-[#8A8A8A]">
                                Last synced: {formatDate(account.last_sync)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => syncGmail(account.account_id)}
                            disabled={syncing}
                            className="p-2 hover:bg-[#2E2E2E] rounded transition-colors"
                            title="Sync now"
                          >
                            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                          </button>
                          <button
                            onClick={() => disconnectGmail(account.account_id)}
                            className="p-2 hover:bg-red-500/20 rounded transition-colors text-red-400"
                            title="Disconnect"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* GA4 Analytics Integration */}
            <div className="card-dashboard">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-500/10 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="font-medium">Google Analytics 4</h3>
                    <p className="text-xs text-[#6B7280]">Website event tracking</p>
                  </div>
                </div>
                <span className="badge bg-green-500/20 text-green-400">Active</span>
              </div>
              <div className="mt-4 p-3 bg-[#1C1C1C]">
                <p className="text-xs text-[#6B7280]">
                  <span className="text-[#F5F4EF]">ID:</span> G-LNPSB69TPD
                </p>
                <p className="text-xs text-[#6B7280] mt-1">
                  Events: contact_submit, book_call_click, pricing_cta_click
                </p>
              </div>
            </div>

            {/* Calendly Integration Placeholder */}
            <div className="card-dashboard opacity-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/10 flex items-center justify-center">
                    <Link2 className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-medium">Calendly</h3>
                    <p className="text-xs text-[#6B7280]">Booking integration</p>
                  </div>
                </div>
                <span className="badge bg-[#2E2E2E] text-[#6B7280]">Planned</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold mb-1">Audit Log</h2>
              <p className="text-sm text-[#6B7280]">Record of data changes</p>
            </div>

            <div className="border border-[#2E2E2E] max-h-[600px] overflow-y-auto scrollbar-custom">
              <table className="w-full">
                <thead className="bg-[#1C1C1C] border-b border-[#2E2E2E] sticky top-0">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Timestamp</th>
                    <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">User</th>
                    <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Action</th>
                    <th className="text-left p-4 text-sm font-medium text-[#8A8A8A]">Entity</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <tr key={i} className="border-b border-[#2E2E2E]">
                        <td colSpan={4} className="p-4"><div className="h-4 bg-[#2E2E2E] rounded animate-pulse" /></td>
                      </tr>
                    ))
                  ) : auditLogs.length === 0 ? (
                    <tr><td colSpan={4} className="p-8 text-center text-[#8A8A8A]">No audit logs yet</td></tr>
                  ) : (
                    auditLogs.map((log) => (
                      <tr key={log.log_id} className="border-b border-[#2E2E2E]">
                        <td className="p-4 text-sm text-[#8A8A8A]">{formatDate(log.created_at)}</td>
                        <td className="p-4 text-sm">{log.user_id}</td>
                        <td className="p-4">
                          <span className={`badge ${
                            log.action === 'create' ? 'bg-green-500/20 text-green-400' :
                            log.action === 'update' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="p-4 text-sm">
                          <span className="capitalize">{log.entity_type}</span>
                          <span className="text-[#8A8A8A] ml-2 text-xs">{log.entity_id}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'gdpr' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold mb-1">Data Protection</h2>
              <p className="text-sm text-[#6B7280]">UK GDPR compliance information</p>
            </div>

            <div className="grid gap-6">
              <div className="card-dashboard">
                <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
                  <Database className="w-4 h-4 text-[#5F8FB8]" />
                  Storage
                </h3>
                <ul className="space-y-2 text-xs text-[#6B7280]">
                  <li>Secure MongoDB database</li>
                  <li>Encrypted at rest and in transit</li>
                  <li>UK/EU data centres</li>
                  <li>Automated backups</li>
                </ul>
              </div>

              <div className="card-dashboard">
                <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#5F8FB8]" />
                  Access Control
                </h3>
                <ul className="space-y-2 text-xs text-[#6B7280]">
                  <li>Role-based access (RBAC)</li>
                  <li>Full audit trail</li>
                  <li>7-day session expiry</li>
                  <li>No public data exposure</li>
                </ul>
              </div>

              <div className="card-dashboard">
                <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[#5F8FB8]" />
                  Data Subject Rights
                </h3>
                <ul className="space-y-2 text-xs text-[#6B7280]">
                  <li>Export available on request</li>
                  <li>Right to erasure supported</li>
                  <li>Processing purposes documented</li>
                  <li>Contact: privacy@orchestrio.co.uk</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Settings;
